import { z } from 'zod';
import { RulesSchema } from './rules';

// WebSocket のメッセージ（spec.md「イベント一覧」）。すべて {type, v, at, payload}。
// v は大会の状態の版番号。端末は数が飛んだら sync.request で取り直す。

export const RoleSchema = z.enum(['host', 'projector', 'participant', 'remote']);
export type Role = z.infer<typeof RoleSchema>;

const id = z.string().min(1).max(64);

export const HostCommandSchema = z.discriminatedUnion('c', [
  z.object({ c: z.literal('screen'), screen: z.enum(['title', 'rules', 'join']) }),
  z.object({ c: z.literal('start') }),
  z.object({ c: z.literal('next') }),
  z.object({ c: z.literal('judge'), correct: z.boolean() }),
  z.object({ c: z.literal('through') }),
  z.object({ c: z.literal('skip') }),
  z.object({ c: z.literal('undo') }),
  z.object({ c: z.literal('pause') }),
  z.object({ c: z.literal('resume') }),
  z.object({ c: z.literal('showRanking') }),
  z.object({ c: z.literal('break'), minutes: z.number().int().min(1).max(60) }),
  z.object({ c: z.literal('finish') }),
  z.object({ c: z.literal('abandon') }),
  z.object({ c: z.literal('adjustScore'), unitId: id, delta: z.number().int().min(-100).max(100) }),
  z.object({ c: z.literal('kick'), participantId: id }),
  z.object({ c: z.literal('rename'), participantId: id, nickname: z.string().trim().min(1).max(10) }),
  z.object({ c: z.literal('moveTeam'), participantId: id, teamId: id }),
  // ここから下はサーバーの設定（engine の状態は変えない）
  z.object({
    c: z.literal('settings'),
    calm: z.boolean().optional(),
    seVolume: z.number().min(0).max(1).optional(),
    bgmVolume: z.number().min(0).max(1).optional(),
    mirror: z.boolean().optional(),
    /** 投影画面の隅に参加用QRコードを出す（大会が始まってからの途中参加用） */
    joinQr: z.boolean().optional(),
  }),
]);
export type HostCommandMsg = z.infer<typeof HostCommandSchema>;

/** 端末 → サーバー */
export const ClientMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('hello'),
    payload: z.object({
      role: RoleSchema,
      tournamentId: z.string().max(64).optional(),
      joinCode: z.string().max(16).optional(),
      deviceKey: z.string().max(100).optional(),
      remoteCode: z.string().max(16).optional(),
    }),
  }),
  z.object({ type: z.literal('time.ping'), payload: z.object({ c0: z.number() }) }),
  z.object({ type: z.literal('net.pong'), payload: z.object({ s0: z.number() }) }),
  z.object({
    type: z.literal('join'),
    payload: z.object({ nickname: z.string().trim().min(1).max(10), teamId: z.string().max(64).nullable() }),
  }),
  z.object({ type: z.literal('buzz'), payload: z.object({ questionSeq: z.number().int(), pressedAt: z.number() }) }),
  z.object({ type: z.literal('host.command'), payload: HostCommandSchema }),
  z.object({
    type: z.literal('playback.report'),
    payload: z.object({
      questionSeq: z.number().int(),
      event: z.enum(['start', 'stop', 'end']),
      posMs: z.number().min(0),
    }),
  }),
  z.object({ type: z.literal('projector.status'), payload: z.object({ audioEnabled: z.boolean() }) }),
  z.object({ type: z.literal('sync.request'), payload: z.object({}).optional() }),
  z.object({ type: z.literal('leave'), payload: z.object({}).optional() }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// ---------------------------------------------------------------- 状態（役割ごとに見せる範囲を絞る）

export const NetQualitySchema = z.enum(['good', 'warn', 'bad']);

export const UnitViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  score: z.number(),
  correct: z.number(),
  wrong: z.number(),
  status: z.enum(['active', 'won', 'lost']),
  wonOrder: z.number().nullable(),
  rank: z.number(),
  resting: z.boolean(),
  restRemaining: z.number(),
  members: z.number(),
});
export type UnitView = z.infer<typeof UnitViewSchema>;

export const ParticipantViewSchema = z.object({
  id: z.string(),
  nickname: z.string(),
  teamId: z.string().nullable(),
  active: z.boolean(),
  connected: z.boolean(),
  net: NetQualitySchema.nullable(),
  rttMs: z.number().nullable(),
  /** ゆらぎ（ms）と、直近10回の途切れの回数（2026-10-11 遅め・不安定の理由を出す） */
  jitterMs: z.number().nullable().optional(),
  lost: z.number().nullable().optional(),
  clockSuspect: z.boolean(),
});
export type ParticipantView = z.infer<typeof ParticipantViewSchema>;

export const BuzzViewSchema = z.object({
  participantId: z.string(),
  unitId: z.string(),
  diffMs: z.number(),
  late: z.boolean(),
});
export type BuzzView = z.infer<typeof BuzzViewSchema>;

export const TimingSchema = z.object({ i: z.number(), t: z.number() });
export type Timing = z.infer<typeof TimingSchema>;

export const QuestionViewSchema = z.object({
  seq: z.number(),
  stage: z.enum(['ready', 'reading', 'thinking', 'collecting', 'answering', 'reveal']),
  openAt: z.number(),
  fromMs: z.number(),
  readPosMs: z.number(),
  readingDone: z.boolean(),
  round: z.number(),
  order: z.array(BuzzViewSchema),
  answerer: z.object({ participantId: z.string(), unitId: z.string(), deadline: z.number() }).nullable(),
  thinkingDeadline: z.number().nullable(),
  eligible: z.array(z.string()),
  lockedUnits: z.array(z.string()),
  lockedParticipants: z.array(z.string()),
  resting: z.array(z.string()),
  correctParticipantId: z.string().nullable(),
  correctUnitId: z.string().nullable(),
  through: z.boolean(),
  /** 投影・主催者・リモコンだけ */
  text: z.string().optional(),
  audioUrl: z.string().nullable().optional(),
  /** 投影画面だけ：問題の前のひとこと（その問題を最初に読むときだけ）。読み始め（openAt）の直前に流し終える */
  intro: z.object({ url: z.string(), durationMs: z.number() }).nullable().optional(),
  /** 問題の後ろのひとこと（投影画面だけ）。問題を最後まで読んだら流す */
  outro: z.object({ url: z.string(), durationMs: z.number() }).nullable().optional(),
  /** 投影画面だけ：正解発表の読み上げ（「正解は」＋答え＋「です。」の順。答えの音声が無ければ出さない） */
  answerVoice: z.array(z.object({ url: z.string(), durationMs: z.number() })).nullable().optional(),
  timings: z.array(TimingSchema).optional(),
  durationMs: z.number().optional(),
  /** 主催者・リモコンは常に、投影と参加者は正解発表のときだけ */
  answer: z.string().optional(),
  answerReading: z.string().optional(),
  answerAlts: z.array(z.string()).optional(),
  explanation: z.string().optional(),
});
export type QuestionView = z.infer<typeof QuestionViewSchema>;

export const GameViewSchema = z.object({
  role: RoleSchema,
  v: z.number(),
  serverNow: z.number(),
  tournament: z.object({
    id: z.string(),
    name: z.string(),
    format: z.enum(['individual', 'team']),
    status: z.enum(['waiting', 'running', 'finished', 'abandoned']),
    screen: z.enum(['title', 'rules', 'join', 'question', 'ranking', 'break', 'final']),
    questionCount: z.number(),
    rules: RulesSchema,
    joinCode: z.string().nullable(),
    paused: z.object({ reason: z.enum(['host', 'projector']) }).nullable(),
    breakUntil: z.number().nullable(),
    startedAt: z.number().nullable(),
    projectorConnected: z.boolean(),
    projectorAudio: z.boolean(),
    settings: z.object({ calm: z.boolean(), seVolume: z.number(), bgmVolume: z.number(), mirror: z.boolean(), joinQr: z.boolean() }),
    /** 主催者だけ */
    remoteCode: z.string().nullable().optional(),
    maxPlayers: z.number(),
  }),
  units: z.array(UnitViewSchema),
  participants: z.array(ParticipantViewSchema),
  question: QuestionViewSchema.nullable(),
  /** 投影画面が裏で読み込む次の問題の音声 */
  preload: z.object({ seq: z.number(), audioUrl: z.string().nullable() }).nullable().optional(),
  lastJudge: z
    .object({
      seq: z.number(),
      correct: z.boolean(),
      participantId: z.string(),
      unitId: z.string(),
      delta: z.number(),
      next: z.enum(['resume', 'nextInQueue', 'reveal']),
      timeout: z.boolean(),
    })
    .nullable(),
  rankPrev: z.record(z.string(), z.number()).nullable(),
  /** 参加者だけ：自分のこと */
  me: z
    .object({
      participantId: z.string(),
      unitId: z.string().nullable(),
      nickname: z.string(),
      canBuzz: z.boolean(),
      buzzed: z.boolean(),
      net: NetQualitySchema.nullable(),
      /** 自分の通信の往復の時間（ms。2026-10-11 速さの目安を出す） */
      rttMs: z.number().nullable().optional(),
      jitterMs: z.number().nullable().optional(),
      lost: z.number().nullable().optional(),
      /** 自分（自チーム）の得点・順位 */
      unit: UnitViewSchema.nullable(),
      /** 同じ順位の数 */
      tie: z.number(),
    })
    .nullable(),
  canUndo: z.boolean(),
});
export type GameView = z.infer<typeof GameViewSchema>;

/** サーバー → 端末 */
export type ServerMessage =
  | { type: 'welcome'; v: number; at: number; payload: { role: Role; participantId: string | null } }
  | { type: 'joined'; v: number; at: number; payload: { participantId: string; deviceKey: string } }
  | { type: 'time.pong'; v: number; at: number; payload: { c0: number; s: number } }
  | { type: 'net.ping'; v: number; at: number; payload: { s0: number } }
  | {
      type: 'net.quality';
      v: number;
      at: number;
      payload: { participantId: string; quality: 'good' | 'warn' | 'bad'; rttMedian: number; rtt: number; jitter: number; lost: number };
    }
  | { type: 'state.snapshot'; v: number; at: number; payload: GameView }
  | { type: 'roster.update'; v: number; at: number; payload: { participants: ParticipantView[] } }
  | { type: 'question.ready'; v: number; at: number; payload: { questionSeq: number } }
  | {
      type: 'question.open';
      v: number;
      at: number;
      payload: { questionSeq: number; openAt: number; fromMs: number; eligible: string[] };
    }
  | { type: 'buzz.first'; v: number; at: number; payload: { questionSeq: number; participantId: string; posMs: number } }
  | {
      type: 'buzz.result';
      v: number;
      at: number;
      payload: { questionSeq: number; order: BuzzView[]; answererId: string; deadline: number };
    }
  | {
      type: 'judge.result';
      v: number;
      at: number;
      payload: { correct: boolean; participantId: string; unitId: string; delta: number; next: string; timeout: boolean };
    }
  | {
      type: 'question.reveal';
      v: number;
      at: number;
      payload: { questionSeq: number; correctParticipantId: string | null; correctUnitId: string | null };
    }
  | { type: 'phase.change'; v: number; at: number; payload: { screen: string } }
  | { type: 'playback.stop'; v: number; at: number; payload: { questionSeq: number } }
  | { type: 'sound'; v: number; at: number; payload: { name: string } }
  | { type: 'error'; v: number; at: number; payload: { code: string; message: string } };

export type ServerMessageType = ServerMessage['type'];

// ---------------------------------------------------------------- 時計合わせ（spec.md「時計合わせ」）

export type ClockSample = { rtt: number; offset: number };

/** time.ping の結果 1回分。c0, c1 は端末の performance.now() 基準、s はサーバー時刻 */
export function clockSample(c0: number, s: number, c1: number): ClockSample {
  return { rtt: c1 - c0, offset: s - (c0 + c1) / 2 };
}

/** rtt がいちばん小さい回の offset を使う */
export function bestOffset(samples: ClockSample[]): ClockSample | null {
  let best: ClockSample | null = null;
  for (const x of samples) if (!best || x.rtt < best.rtt) best = x;
  return best;
}
